import React, { useState, useEffect } from 'react';
import Picker from 'react-mobile-picker';
import './PeriodDayPicker.css';

interface PeriodDayPickerProps {
  period: string;
  day: string;
  onChange: (period: string, day: string) => void;
  isOpen: boolean;
  onClose: () => void;
}

const PeriodDayPicker: React.FC<PeriodDayPickerProps> = ({
  period,
  day,
  onChange,
  isOpen,
  onClose
}) => {
  const [pickerValue, setPickerValue] = useState({
    period: period || '每周',
    day: day || '周一'
  });
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  // 周期选项
  const periodOptions = ['每天', '每周', '每两周', '每月'];
  
  // 根据周期获取日期选项
  const getDayOptions = (selectedPeriod: string) => {
    switch (selectedPeriod) {
      case '每天':
        return ['']; // 每天显示空选项
      case '每周':
      case '每两周':
        return ['周一', '周二', '周三', '周四', '周五'];
      case '每月':
        return Array.from({ length: 31 }, (_, i) => `${i + 1}日`);
      default:
        return [''];
    }
  };

  const dayOptions = getDayOptions(pickerValue.period);

  // 当外部值变化时更新picker值
  useEffect(() => {
    // 转换后端格式到前端显示格式
    const periodMapping: { [key: string]: string } = {
      'daily': '每天',
      'weekly': '每周',
      'biweekly': '每两周',
      'monthly': '每月'
    };
    
    setPickerValue({
      period: periodMapping[period] || '每周',
      day: day || '周一'
    });
  }, [period, day]);

  // 控制动画和渲染
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      setTimeout(() => setShouldRender(false), 200); // 等待动画完成
    }
  }, [isOpen]);

  // 处理picker值变化
  const handlePickerChange = (newValue: any, key: string) => {
    let updatedValue = { ...newValue };
    
    // 如果周期变化，需要重置日期选择
    if (key === 'period') {
      const newDayOptions = getDayOptions(updatedValue.period);
      updatedValue.day = newDayOptions[0]; // 选择第一个可用选项
    }
    
    setPickerValue(updatedValue);
  };

  // 确认选择
  const handleConfirm = () => {
    // 转换为后端期望的格式
    const periodMapping: { [key: string]: string } = {
      '每天': 'daily',
      '每周': 'weekly', 
      '每两周': 'biweekly',
      '每月': 'monthly'
    };
    
    const backendPeriod = periodMapping[pickerValue.period];
    const backendDay = pickerValue.day;
    
    onChange(backendPeriod, backendDay);
    onClose();
  };

  // 取消选择
  const handleCancel = () => {
    // 重置为原始值
    const periodMapping: { [key: string]: string } = {
      'daily': '每天',
      'weekly': '每周',
      'biweekly': '每两周',
      'monthly': '每月'
    };
    
    setPickerValue({
      period: periodMapping[period] || '每周',
      day: day || '周一'
    });
    onClose();
  };

  if (!shouldRender) return null;

  return (
    <div className="period-day-picker-overlay">
      <div className={`period-day-picker-modal ${isAnimating ? 'slide-up' : 'slide-down'}`}>
        {/* 头部按钮 */}
        <div className="period-day-picker-header">
          <button 
            className="period-day-picker-cancel"
            onClick={handleCancel}
          >
            取消
          </button>
          <div className="period-day-picker-title">
            选择周期
          </div>
          <button 
            className="period-day-picker-confirm"
            onClick={handleConfirm}
          >
            确认
          </button>
        </div>
        
        {/* 滚动选择器 */}
        <div className="period-day-picker-content">
          <Picker
            value={pickerValue}
            onChange={handlePickerChange}
            height={216}
            itemHeight={36}
            wheelMode="natural"
          >
            {/* 周期列 */}
            <Picker.Column name="period">
              {periodOptions.map(periodOption => (
                <Picker.Item key={periodOption} value={periodOption}>
                  {({ selected }) => (
                    <div className={`picker-item ${selected ? 'selected' : ''}`}>
                      {periodOption}
                    </div>
                  )}
                </Picker.Item>
              ))}
            </Picker.Column>
            
            {/* 日期列 - 始终显示 */}
            <Picker.Column name="day">
              {dayOptions.map((dayOption, index) => (
                <Picker.Item key={`${dayOption}-${index}`} value={dayOption}>
                  {({ selected }) => (
                    <div className={`picker-item ${selected ? 'selected' : ''}`}>
                      {dayOption}
                    </div>
                  )}
                </Picker.Item>
              ))}
            </Picker.Column>
          </Picker>
        </div>
      </div>
    </div>
  );
};

export default PeriodDayPicker;